import { useMemo, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ArrowLeft,
  Loader2,
  Plus,
  Trash2,
  Trophy,
  Users,
  Shuffle,
  CheckCircle2,
  Flag,
  Pencil,
  Medal,
  Settings,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { resolveAssetUrl } from "@/lib/assets";
import { cn } from "@/lib/utils";

const QUICK_RESULTS = [
  { a: 6, b: 0 }, { a: 6, b: 2 }, { a: 6, b: 4 }, { a: 7, b: 5 }, { a: 7, b: 6 },
] as const;
const SCORE_OPTIONS = [0, 1, 2, 3, 4, 5, 6, 7];

interface Member {
  userId: string;
  userName: string;
  userImage?: string;
  role: string;
}

interface PairPlayer {
  id: string;
  name: string;
  image: string | null;
}

interface Pair {
  id: string;
  name: string;
  player1Id: string | null;
  player2Id: string | null;
  player1: PairPlayer | null;
  player2: PairPlayer | null;
}

interface TMatch {
  id: string;
  tournamentId: string;
  pairAId: string | null;
  pairBId: string | null;
  winnerId: string | null;
  round: number;
  matchNumber: number;
  phase: "group" | "knockout";
  groupNumber: number | null;
  score: { sets: Array<{ pairA: number; pairB: number }> } | null;
  status: "pending" | "completed" | "cancelled";
  recordedById: string | null;
  recordedAt: string | null;
  editedById: string | null;
  editedAt: string | null;
}

interface Tournament {
  id: string;

  name: string;

  description: string | null;

  format: "single_elimination" | "groups_elimination";

  feedsElo: boolean;

  status: "draft" | "in_progress" | "completed" | "cancelled";

  startDate: string | null;
}

function formatStartDate(dateStr: string) {
  const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return dateStr;
  const [, y, m, d] = match.map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("es-ES", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}
interface StandingRow {
  pairId: string;
  played: number;
  wins: number;
  losses: number;
  gamesWon: number;
  gamesLost: number;
  gamesDiff: number;
  position: number;
  decidedByDraw: boolean;
}

interface StandingGroup {
  groupNumber: number;
  rows: StandingRow[];
}

interface DetailData {
  tournament: Tournament;
  pairs: Pair[];
  matches: TMatch[];
  standings: StandingGroup[];
  groupStageComplete: boolean;
  hasKnockout: boolean;
  isAdmin: boolean;
}

interface DrawPreviewGroups {
  format: "groups_elimination";
  groups: string[][];
}
interface DrawPreviewSingle {
  format: "single_elimination";
  slots: (string | null)[];
  bracketSize: number;
  byes: number;
}
type DrawPreview = DrawPreviewGroups | DrawPreviewSingle;

interface KnockoutPreview {
  qualifiers: string[];
  groupWinners: string[];
  runnersUp: string[];
  bracketSize: number;
  byes: number;
  slots: (string | null)[];
}

const statusLabels: Record<Tournament["status"], string> = {
  draft: "Borrador",
  in_progress: "En progreso",
  completed: "Finalizado",
  cancelled: "Cancelado",
};

const statusColors: Record<Tournament["status"], string> = {
  draft: "bg-muted text-muted-foreground",
  in_progress: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
  completed: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400",
  cancelled: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
};

function formatLabel(format: Tournament["format"]) {
  return format === "groups_elimination" ? "Grupos + Eliminación" : "Eliminación simple";
}

function roundName(round: number, totalRounds: number) {
  const fromEnd = totalRounds - round;
  if (fromEnd === 0) return "Final";
  if (fromEnd === 1) return "Semifinales";
  if (fromEnd === 2) return "Cuartos de final";
  if (fromEnd === 3) return "Octavos de final";
  return `Ronda ${round}`;
}

interface Props {
  leagueId: string;
  tournamentId: string;
  members: Member[];
  onBack: () => void;
}

export function LeagueTournamentDetail({ leagueId, tournamentId, members, onBack }: Props) {
  const { toast } = useToast();
  const baseKey = ["/api/leagues", leagueId, "tournaments", tournamentId];
  const invalidate = () => queryClient.invalidateQueries({ queryKey: baseKey });

  const { data, isLoading } = useQuery<DetailData>({ queryKey: baseKey });

  const [player1Id, setPlayer1Id] = useState("");
  const [player2Id, setPlayer2Id] = useState("");
  const [deletePairId, setDeletePairId] = useState<string | null>(null);

  // Settings dialog state
  const [showSettings, setShowSettings] = useState(false);
  const [editName, setEditName] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [editStartDate, setEditStartDate] = useState("");

  // Draw preview local state
  const [drawPreview, setDrawPreview] = useState<DrawPreview | null>(null);
  // Knockout preview local state
  const [knockoutPreview, setKnockoutPreview] = useState<KnockoutPreview | null>(null);

  const [resultMatch, setResultMatch] = useState<TMatch | null>(null);
  const [resultMode, setResultMode] = useState<"record" | "edit">("record");
  const [sets, setSets] = useState<Array<{ a: string; b: string }>>([
    { a: "", b: "" },
    { a: "", b: "" },
    { a: "", b: "" },
  ]);
  const [showFinalize, setShowFinalize] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [swapSlotA, setSwapSlotA] = useState<number | null>(null);

  const memberName = (userId: string | null | undefined) => {
    if (!userId) return "—";
    return members.find((m) => m.userId === userId)?.userName ?? "Jugador";
  };

  const pairById = useMemo(() => {
    const map = new Map<string, Pair>();
    (data?.pairs ?? []).forEach((p) => map.set(p.id, p));
    return map;
  }, [data?.pairs]);

  const pairName = (pairId: string | null) => {
    if (!pairId) return "Por definir";
    return pairById.get(pairId)?.name ?? "Pareja";
  };

  // ---- Mutations ----
  const addPairMutation = useMutation({
    mutationFn: async () =>
      apiRequest("POST", `/api/leagues/${leagueId}/tournaments/${tournamentId}/pairs`, {
        player1Id,
        player2Id,
      }),
    onSuccess: () => {
      invalidate();
      setPlayer1Id("");
      setPlayer2Id("");
      toast({ title: "Pareja añadida" });
    },
    onError: (e: any) =>
      toast({ title: "Error", description: e?.message?.replace(/^\d+:\s*/, "") || "No se pudo añadir la pareja", variant: "destructive" }),
  });

  const deletePairMutation = useMutation({
    mutationFn: async (pairId: string) =>
      apiRequest("DELETE", `/api/leagues/${leagueId}/tournaments/${tournamentId}/pairs/${pairId}`),
    onSuccess: () => {
      invalidate();
      setDeletePairId(null);
      toast({ title: "Pareja eliminada" });
    },
    onError: (e: any) =>
      toast({ title: "Error", description: e?.message?.replace(/^\d+:\s*/, "") || "No se pudo eliminar", variant: "destructive" }),
  });

  const drawPreviewMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/leagues/${leagueId}/tournaments/${tournamentId}/draw/preview`);
      return (await res.json()) as DrawPreview;
    },
    onSuccess: (preview) => setDrawPreview(preview),
    onError: (e: any) =>
      toast({ title: "Error", description: e?.message?.replace(/^\d+:\s*/, "") || "No se pudo generar el sorteo", variant: "destructive" }),
  });

  const drawConfirmMutation = useMutation({
    mutationFn: async (body: { groups: string[][] } | { slots: (string | null)[] }) =>
      apiRequest("POST", `/api/leagues/${leagueId}/tournaments/${tournamentId}/draw/confirm`, body),
    onSuccess: () => {
      invalidate();
      setDrawPreview(null);
      toast({ title: "Sorteo confirmado" });
    },
    onError: (e: any) =>
      toast({ title: "Error", description: e?.message?.replace(/^\d+:\s*/, "") || "No se pudo confirmar el sorteo", variant: "destructive" }),
  });

  const knockoutPreviewMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/leagues/${leagueId}/tournaments/${tournamentId}/knockout/preview`);
      return (await res.json()) as KnockoutPreview;
    },
    onSuccess: (preview) => setKnockoutPreview(preview),
    onError: (e: any) =>
      toast({ title: "Error", description: e?.message?.replace(/^\d+:\s*/, "") || "No se pudo generar la eliminatoria", variant: "destructive" }),
  });

  const knockoutConfirmMutation = useMutation({
    mutationFn: async (slots: (string | null)[]) =>
      apiRequest("POST", `/api/leagues/${leagueId}/tournaments/${tournamentId}/knockout/confirm`, { slots }),
    onSuccess: () => {
      invalidate();
      setKnockoutPreview(null);
      toast({ title: "Eliminatoria generada" });
    },
    onError: (e: any) =>
      toast({ title: "Error", description: e?.message?.replace(/^\d+:\s*/, "") || "No se pudo confirmar", variant: "destructive" }),
  });

  const resultMutation = useMutation({
    mutationFn: async ({ matchId, mode, body }: { matchId: string; mode: "record" | "edit"; body: { sets: Array<{ pairA: number; pairB: number }> } }) =>
      apiRequest(
        mode === "record" ? "POST" : "PATCH",
        `/api/leagues/${leagueId}/tournaments/${tournamentId}/matches/${matchId}/result`,
        body,
      ),
    onSuccess: () => {
      invalidate();
      setResultMatch(null);
      toast({ title: resultMode === "record" ? "Resultado registrado" : "Resultado actualizado" });
    },
    onError: (e: any) =>
      toast({ title: "Error", description: e?.message?.replace(/^\d+:\s*/, "") || "No se pudo guardar el resultado", variant: "destructive" }),
  });

  const thirdPlaceMutation = useMutation({
    mutationFn: async () =>
      apiRequest("POST", `/api/leagues/${leagueId}/tournaments/${tournamentId}/third-place`),
    onSuccess: () => {
      invalidate();
      toast({ title: "Partido por el 3.er puesto generado" });
    },
    onError: (e: any) =>
      toast({ title: "Error", description: e?.message?.replace(/^\d+:\s*/, "") || "No se pudo generar el partido por el 3.er puesto", variant: "destructive" }),
  });

  const finalizeMutation = useMutation({
    mutationFn: async () =>
      apiRequest("PATCH", `/api/leagues/${leagueId}/tournaments/${tournamentId}`, { status: "completed" }),
    onSuccess: () => {
      invalidate();
      setShowFinalize(false);
      toast({ title: "Torneo finalizado" });
    },
    onError: (e: any) =>
      toast({ title: "Error", description: e?.message?.replace(/^\d+:\s*/, "") || "No se pudo finalizar", variant: "destructive" }),
  });

  const deleteTournamentMutation = useMutation({
    mutationFn: async () =>
      apiRequest("DELETE", `/api/leagues/${leagueId}/tournaments/${tournamentId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/leagues", leagueId, "tournaments"] });
      toast({ title: "Torneo eliminado" });
      onBack();
    },
    onError: (e: any) =>
      toast({ title: "Error", description: e?.message?.replace(/^\d+:\s*/, "") || "No se pudo eliminar el torneo", variant: "destructive" }),
  });

  const settingsMutation = useMutation({
    mutationFn: async (body: { name: string; description: string | null; startDate?: string }) =>
      apiRequest("PATCH", `/api/leagues/${leagueId}/tournaments/${tournamentId}`, body),
    onSuccess: () => {
      invalidate();
      setShowSettings(false);
      toast({ title: "Torneo actualizado" });
    },
    onError: (e: any) =>
      toast({ title: "Error", description: e?.message?.replace(/^\d+:\s*/, "") || "No se pudo actualizar el torneo", variant: "destructive" }),
  });

  const openSettings = () => {
    setEditName(tournament.name);
    setEditDesc(tournament.description ?? "");
    setEditStartDate(tournament.startDate ? tournament.startDate.substring(0, 10) : "");
    setShowSettings(true);
  };

  const submitSettings = () => {
    const trimmedName = editName.trim();
    if (!trimmedName) {
      toast({ title: "Error", description: "El nombre es obligatorio", variant: "destructive" });
      return;
    }
    settingsMutation.mutate({
      name: trimmedName,
      description: editDesc.trim() || null,
      ...(editStartDate ? { startDate: editStartDate } : {}),
    });
  };

  if (isLoading || !data) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const { tournament, pairs, matches, standings, groupStageComplete, hasKnockout, isAdmin } = data;
  const isDraft = tournament.status === "draft";
  const isCompleted = tournament.status === "completed";
  const isCancelled = tournament.status === "cancelled";
  const editable = isAdmin && !isCompleted && !isCancelled;

  const pairedPlayerIds = new Set<string>();
  pairs.forEach((p) => {
    if (p.player1Id) pairedPlayerIds.add(p.player1Id);
    if (p.player2Id) pairedPlayerIds.add(p.player2Id);
  });
  const availableForP1 = members.filter((m) => !pairedPlayerIds.has(m.userId) && m.userId !== player2Id);
  const availableForP2 = members.filter((m) => !pairedPlayerIds.has(m.userId) && m.userId !== player1Id);

  const groupMatches = matches.filter((m) => m.phase === "group");
  const knockoutMatches = matches.filter((m) => m.phase === "knockout");
  const maxKnockoutRound = knockoutMatches.length ? Math.max(...knockoutMatches.map((m) => m.round)) : 0;
  // The 3rd-place match lives in the final round with matchNumber 2 (the
  // final itself is always matchNumber 1).
  const thirdPlaceMatch =
    knockoutMatches.find((m) => m.round === maxKnockoutRound && m.matchNumber === 2) ?? null;
  const bracketMatches = thirdPlaceMatch
    ? knockoutMatches.filter((m) => m.id !== thirdPlaceMatch.id)
    : knockoutMatches;
  const knockoutRounds = Array.from(new Set(bracketMatches.map((m) => m.round))).sort((a, b) => a - b);
  const totalKnockoutRounds = knockoutRounds.length;
  const semifinals =
    maxKnockoutRound >= 2 ? bracketMatches.filter((m) => m.round === maxKnockoutRound - 1) : [];
  const canGenerateThirdPlace =
    editable &&
    tournament.status === "in_progress" &&
    !thirdPlaceMatch &&
    semifinals.length === 2 &&
    semifinals.every((m) => m.status === "completed" && m.winnerId && m.pairAId && m.pairBId);

  const openResult = (match: TMatch, mode: "record" | "edit") => {
    setResultMatch(match);
    setResultMode(mode);
    if (mode === "edit" && match.score?.sets?.length) {
      setSets(match.score.sets.map((s) => ({ a: String(s.pairA), b: String(s.pairB) })));
    } else {
      setSets([{ a: "", b: "" }, { a: "", b: "" }]);
    }
  };

  const submitResult = () => {
    if (!resultMatch) return;
    const validSets = sets
      .filter((s) => s.a !== "" && s.b !== "")
      .map((s) => ({ pairA: parseInt(s.a, 10), pairB: parseInt(s.b, 10) }));
    if (validSets.length === 0) {
      toast({ title: "Error", description: "Ingresa al menos un set", variant: "destructive" });
      return;
    }
    resultMutation.mutate({ matchId: resultMatch.id, mode: resultMode, body: { sets: validSets } });
  };

  // ---- Draw preview manipulation helpers ----
  const movePairToGroup = (pairId: string, targetGroupIndex: number) => {
    if (!drawPreview || drawPreview.format !== "groups_elimination") return;
    const groups = drawPreview.groups.map((g) => g.filter((id) => id !== pairId));
    groups[targetGroupIndex] = [...groups[targetGroupIndex], pairId];
    setDrawPreview({ ...drawPreview, groups });
  };

  const swapSlots = (index: number, previewSlots: (string | null)[], setter: (s: (string | null)[]) => void) => {
    if (swapSlotA === null) {
      setSwapSlotA(index);
      return;
    }
    if (swapSlotA === index) {
      setSwapSlotA(null);
      return;
    }
    const next = [...previewSlots];
    const tmp = next[swapSlotA];
    next[swapSlotA] = next[index];
    next[index] = tmp;
    setter(next);
    setSwapSlotA(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start gap-3 flex-wrap">
        <Button variant="ghost" size="icon" onClick={onBack} data-testid="button-tournament-back">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-xl font-bold" data-testid="text-tournament-name">{tournament.name}</h2>
            <Badge className={statusColors[tournament.status]} data-testid="badge-tournament-status">
              {statusLabels[tournament.status]}
            </Badge>
            {tournament.feedsElo && (
              <Badge variant="outline" className="border-amber-400 text-amber-700 dark:text-amber-400">
                Cuenta para ELO
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground mt-1">{formatLabel(tournament.format)}</p>
          {tournament.startDate && (
            <p className="text-sm text-muted-foreground mt-0.5" data-testid="text-tournament-start-date">
              Inicio: {formatStartDate(tournament.startDate)}
            </p>
          )}
          {tournament.description && (
            <p className="text-sm text-muted-foreground mt-1">{tournament.description}</p>
          )}
        </div>
        {editable && (
          <Button
            variant="ghost"
            size="icon"
            onClick={openSettings}
            data-testid="button-tournament-settings"
            title="Editar torneo"
          >
            <Settings className="h-4 w-4" />
          </Button>
        )}
        {isAdmin && !isCompleted && !isCancelled && (
          <Button
            variant="ghost"
            size="icon"
            className="text-destructive hover:text-destructive hover:bg-destructive/10"
            onClick={() => setShowDelete(true)}
            data-testid="button-delete-tournament"
            title="Eliminar torneo"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
        {editable && tournament.status === "in_progress" && (
          <Button
            variant="outline"
            size="sm"
            className="gap-1"
            onClick={() => setShowFinalize(true)}
            data-testid="button-finalize-tournament"
          >
            <Flag className="h-4 w-4" />
            Finalizar torneo
          </Button>
        )}
      </div>

      {/* Podium (completed) */}
      {isCompleted && knockoutMatches.length > 0 && (
        <TournamentPodiumCard knockoutMatches={knockoutMatches} pairById={pairById} />
      )}

      {/* Pairs section (draft) */}
      {isDraft && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="h-4 w-4" /> Parejas ({pairs.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {isAdmin && (
              <div className="flex flex-col sm:flex-row gap-2 items-end">
                <div className="flex-1 w-full space-y-1">
                  <Label className="text-xs">Jugador 1</Label>
                  <Select value={player1Id} onValueChange={setPlayer1Id}>
                    <SelectTrigger data-testid="select-pair-player1">
                      <SelectValue placeholder="Selecciona jugador" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableForP1.map((m) => (
                        <SelectItem key={m.userId} value={m.userId}>{m.userName}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1 w-full space-y-1">
                  <Label className="text-xs">Jugador 2</Label>
                  <Select value={player2Id} onValueChange={setPlayer2Id}>
                    <SelectTrigger data-testid="select-pair-player2">
                      <SelectValue placeholder="Selecciona jugador" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableForP2.map((m) => (
                        <SelectItem key={m.userId} value={m.userId}>{m.userName}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  className="gap-1 w-full sm:w-auto"
                  disabled={!player1Id || !player2Id || addPairMutation.isPending}
                  onClick={() => addPairMutation.mutate()}
                  data-testid="button-add-pair"
                >
                  {addPairMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                  Añadir
                </Button>
              </div>
            )}

            {pairs.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                Aún no hay parejas inscritas.
              </p>
            ) : (
              <div className="space-y-2">
                {pairs.map((p) => (
                  <div
                    key={p.id}
                    className="flex items-center justify-between gap-2 p-2 rounded-md border"
                    data-testid={`row-pair-${p.id}`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="flex -space-x-2">
                        {[p.player1, p.player2].map((pl, i) => (
                          <Avatar key={i} className="h-7 w-7 border-2 border-background">
                            <AvatarImage src={resolveAssetUrl(pl?.image ?? undefined)} />
                            <AvatarFallback className="text-xs">{pl?.name?.charAt(0) ?? "?"}</AvatarFallback>
                          </Avatar>
                        ))}
                      </div>
                      <span className="text-sm font-medium truncate">{p.name}</span>
                    </div>
                    {isAdmin && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 shrink-0"
                        onClick={() => setDeletePairId(p.id)}
                        data-testid={`button-delete-pair-${p.id}`}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {isAdmin && pairs.length >= 2 && !drawPreview && (
              <Button
                className="w-full gap-1"
                onClick={() => drawPreviewMutation.mutate()}
                disabled={drawPreviewMutation.isPending}
                data-testid="button-generate-draw"
              >
                {drawPreviewMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Shuffle className="h-4 w-4" />}
                Generar sorteo
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Draw preview */}
      {isDraft && drawPreview && (
        <Card className="border-primary/40">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Vista previa del sorteo</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {drawPreview.format === "groups_elimination" ? (
              <div className="grid gap-3 sm:grid-cols-2">
                {drawPreview.groups.map((group, gi) => (
                  <div key={gi} className="rounded-md border p-3 space-y-2" data-testid={`draw-group-${gi}`}>
                    <p className="text-sm font-semibold">Grupo {gi + 1}</p>
                    {group.map((pid) => (
                      <div key={pid} className="flex items-center justify-between gap-2 text-sm">
                        <span className="truncate">{pairName(pid)}</span>
                        <Select
                          value={String(gi)}
                          onValueChange={(v) => movePairToGroup(pid, parseInt(v, 10))}
                        >
                          <SelectTrigger className="h-7 w-24 text-xs" data-testid={`select-move-pair-${pid}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {drawPreview.groups.map((_, ti) => (
                              <SelectItem key={ti} value={String(ti)}>Grupo {ti + 1}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">
                  Toca dos posiciones para intercambiarlas. {drawPreview.byes} bye(s).
                </p>
                <div className="grid gap-2 sm:grid-cols-2">
                  {drawPreview.slots.map((slot, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() =>
                        swapSlots(i, drawPreview.slots, (next) =>
                          setDrawPreview({ ...drawPreview, slots: next }),
                        )
                      }
                      className={cn(
                        "flex items-center gap-2 rounded-md border p-2 text-sm text-left",
                        swapSlotA === i && "border-primary ring-2 ring-primary/40",
                        slot === null && "text-muted-foreground italic",
                      )}
                      data-testid={`draw-slot-${i}`}
                    >
                      <span className="text-xs text-muted-foreground w-5">{i + 1}</span>
                      <span className="truncate">{slot === null ? "BYE" : pairName(slot)}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setDrawPreview(null)} data-testid="button-cancel-draw">
                Cancelar
              </Button>
              <Button
                className="flex-1 gap-1"
                disabled={drawConfirmMutation.isPending}
                onClick={() =>
                  drawConfirmMutation.mutate(
                    drawPreview.format === "groups_elimination"
                      ? { groups: drawPreview.groups.filter((g) => g.length > 0) }
                      : { slots: drawPreview.slots },
                  )
                }
                data-testid="button-confirm-draw"
              >
                {drawConfirmMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                Confirmar sorteo
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Group standings + matches */}
      {tournament.format === "groups_elimination" && standings.length > 0 && (
        <div className="space-y-4">
          {standings.map((sg) => {
            const gm = groupMatches.filter((m) => m.groupNumber === sg.groupNumber);
            return (
              <Card key={sg.groupNumber} data-testid={`card-group-${sg.groupNumber}`}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Grupo {sg.groupNumber}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Pareja</TableHead>
                        <TableHead className="text-center w-10">PJ</TableHead>
                        <TableHead className="text-center w-10">PG</TableHead>
                        <TableHead className="text-center w-10">PP</TableHead>
                        <TableHead className="text-center w-12">Dif</TableHead>
                        <TableHead className="text-center w-12">JG</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sg.rows.map((row) => (
                        <TableRow key={row.pairId} data-testid={`standing-row-${row.pairId}`}>
                          <TableCell className="font-medium">
                            <span className="text-muted-foreground mr-1">{row.position}.</span>
                            {pairName(row.pairId)}
                            {row.decidedByDraw && <span className="text-xs text-muted-foreground ml-1">(sorteo)</span>}
                          </TableCell>
                          <TableCell className="text-center">{row.played}</TableCell>
                          <TableCell className="text-center">{row.wins}</TableCell>
                          <TableCell className="text-center">{row.losses}</TableCell>
                          <TableCell className="text-center">{row.gamesDiff > 0 ? `+${row.gamesDiff}` : row.gamesDiff}</TableCell>
                          <TableCell className="text-center">{row.gamesWon}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>

                  <div className="space-y-1.5">
                    {gm.map((m) => (
                      <MatchRow
                        key={m.id}
                        match={m}
                        pairName={pairName}
                        editable={editable && tournament.status === "in_progress"}
                        onResult={openResult}
                        memberName={memberName}
                      />
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Generate knockout */}
      {isAdmin && tournament.format === "groups_elimination" && groupStageComplete && !hasKnockout && !isCompleted && !knockoutPreview && (
        <Card className="border-primary/40 bg-primary/5">
          <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
            <div>
              <p className="font-medium">Fase de grupos completada</p>
              <p className="text-sm text-muted-foreground">Genera el cuadro eliminatorio con los clasificados.</p>
            </div>
            <Button
              className="gap-1"
              onClick={() => knockoutPreviewMutation.mutate()}
              disabled={knockoutPreviewMutation.isPending}
              data-testid="button-generate-knockout"
            >
              {knockoutPreviewMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trophy className="h-4 w-4" />}
              Generar eliminatoria
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Knockout preview */}
      {knockoutPreview && (
        <Card className="border-primary/40">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Clasificados a la eliminatoria</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-xs text-muted-foreground">
              Toca dos posiciones para intercambiarlas. {knockoutPreview.byes} bye(s).
            </p>
            <div className="grid gap-2 sm:grid-cols-2">
              {knockoutPreview.slots.map((slot, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() =>
                    swapSlots(i, knockoutPreview.slots, (next) =>
                      setKnockoutPreview({ ...knockoutPreview, slots: next }),
                    )
                  }
                  className={cn(
                    "flex items-center gap-2 rounded-md border p-2 text-sm text-left",
                    swapSlotA === i && "border-primary ring-2 ring-primary/40",
                    slot === null && "text-muted-foreground italic",
                  )}
                  data-testid={`knockout-slot-${i}`}
                >
                  <span className="text-xs text-muted-foreground w-5">{i + 1}</span>
                  <span className="truncate">{slot === null ? "BYE" : pairName(slot)}</span>
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setKnockoutPreview(null)} data-testid="button-cancel-knockout">
                Cancelar
              </Button>
              <Button
                className="flex-1 gap-1"
                disabled={knockoutConfirmMutation.isPending}
                onClick={() => knockoutConfirmMutation.mutate(knockoutPreview.slots)}
                data-testid="button-confirm-knockout"
              >
                {knockoutConfirmMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                Confirmar eliminatoria
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Generate 3rd-place match */}
      {canGenerateThirdPlace && (
        <Card className="border-amber-300/60 bg-amber-50/40 dark:bg-amber-950/20">
          <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
            <div>
              <p className="font-medium">Semifinales completadas</p>
              <p className="text-sm text-muted-foreground">
                Genera un partido por el 3.er puesto entre los perdedores de las semifinales para completar el podio.
              </p>
            </div>
            <Button
              variant="outline"
              className="gap-1"
              onClick={() => thirdPlaceMutation.mutate()}
              disabled={thirdPlaceMutation.isPending}
              data-testid="button-generate-third-place"
            >
              {thirdPlaceMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Medal className="h-4 w-4" />}
              Generar 3.er puesto
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Knockout bracket */}
      {knockoutMatches.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Trophy className="h-4 w-4" /> Cuadro eliminatorio
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4 overflow-x-auto pb-2" data-testid="bracket-scroll">
              {knockoutRounds.map((round) => {
                const rm = bracketMatches
                  .filter((m) => m.round === round)
                  .sort((a, b) => a.matchNumber - b.matchNumber);
                return (
                  <div key={round} className="min-w-[220px] flex-shrink-0 space-y-2">
                    <p className="text-sm font-semibold text-center">{roundName(round, totalKnockoutRounds)}</p>
                    {rm.map((m) => (
                      <BracketMatch
                        key={m.id}
                        match={m}
                        pairName={pairName}
                        editable={editable && tournament.status === "in_progress"}
                        onResult={openResult}
                        memberName={memberName}
                      />
                    ))}
                  </div>
                );
              })}
              {thirdPlaceMatch && (
                <div className="min-w-[220px] flex-shrink-0 space-y-2" data-testid="bracket-third-place">
                  <p className="text-sm font-semibold text-center">3.er puesto</p>
                  <BracketMatch
                    match={thirdPlaceMatch}
                    pairName={pairName}
                    editable={editable && tournament.status === "in_progress"}
                    onResult={openResult}
                    memberName={memberName}
                  />
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Delete pair confirm */}
      <AlertDialog open={!!deletePairId} onOpenChange={(o) => !o && setDeletePairId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar pareja?</AlertDialogTitle>
            <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deletePairId && deletePairMutation.mutate(deletePairId)}
              data-testid="button-confirm-delete-pair"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Finalize confirm */}
      <AlertDialog open={showFinalize} onOpenChange={setShowFinalize}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Finalizar torneo?</AlertDialogTitle>
            <AlertDialogDescription>
              Una vez finalizado no podrás registrar ni editar más resultados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => finalizeMutation.mutate()} data-testid="button-confirm-finalize">
              Finalizar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Settings dialog */}
      <Dialog open={showSettings} onOpenChange={(o) => { if (!o) setShowSettings(false); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar torneo</DialogTitle>
            <DialogDescription>
              Actualiza el nombre, descripción y fecha de inicio del torneo.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <Label htmlFor="edit-tournament-name">Nombre</Label>
              <Input
                id="edit-tournament-name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                maxLength={120}
                data-testid="input-edit-tournament-name"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="edit-tournament-desc">Descripción <span className="text-muted-foreground text-xs">(opcional)</span></Label>
              <Input
                id="edit-tournament-desc"
                value={editDesc}
                onChange={(e) => setEditDesc(e.target.value)}
                maxLength={500}
                data-testid="input-edit-tournament-desc"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="edit-tournament-start-date">Fecha de inicio</Label>
              <Input
                id="edit-tournament-start-date"
                type="date"
                value={editStartDate}
                onChange={(e) => setEditStartDate(e.target.value)}
                data-testid="input-edit-tournament-start-date"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettings(false)}>Cancelar</Button>
            <Button
              onClick={submitSettings}
              disabled={settingsMutation.isPending}
              data-testid="button-save-tournament-settings"
            >
              {settingsMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Result dialog */}
      <Dialog open={!!resultMatch} onOpenChange={(o) => !o && setResultMatch(null)}>
        <DialogContent className="flex flex-col max-h-[90dvh]">
          <DialogHeader className="shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Trophy className="h-4 w-4 text-primary" />
              {resultMode === "record" ? "Registrar resultado" : "Editar resultado"}
            </DialogTitle>
          </DialogHeader>
          {resultMatch && (() => {
            const nameA = pairName(resultMatch.pairAId);
            const nameB = pairName(resultMatch.pairBId);
            const completeSets = sets.filter((s) => s.a !== "" && s.b !== "");
            const aWins = completeSets.filter((s) => Number(s.a) > Number(s.b)).length;
            const bWins = completeSets.filter((s) => Number(s.b) > Number(s.a)).length;
            const winnerLabel = completeSets.length > 0 && aWins !== bWins
              ? aWins > bWins ? `${nameA} gana ${aWins}-${bWins}` : `${nameB} gana ${bWins}-${aWins}`
              : completeSets.length > 0 ? "Empate en sets" : null;
            return (
              <div className="space-y-3 py-1 overflow-y-auto flex-1 min-h-0 pr-1">
                {/* Pair identity pills */}
                <div className="grid grid-cols-[1fr_auto_1fr] gap-2 items-center">
                  <div className="rounded-lg border px-3 py-2 text-center bg-blue-50 dark:bg-blue-950/40 border-blue-200 dark:border-blue-800">
                    <div className="flex items-center justify-center gap-1.5">
                      <span className="w-2 h-2 rounded-full shrink-0 bg-blue-500" />
                      <p className="text-sm font-semibold leading-tight line-clamp-2 text-blue-700 dark:text-blue-400">{nameA}</p>
                    </div>
                  </div>
                  <span className="text-sm font-bold text-muted-foreground">vs</span>
                  <div className="rounded-lg border px-3 py-2 text-center bg-orange-50 dark:bg-orange-950/40 border-orange-200 dark:border-orange-800">
                    <div className="flex items-center justify-center gap-1.5">
                      <span className="w-2 h-2 rounded-full shrink-0 bg-orange-500" />
                      <p className="text-sm font-semibold leading-tight line-clamp-2 text-orange-700 dark:text-orange-400">{nameB}</p>
                    </div>
                  </div>
                </div>

                {/* Sets */}
                {sets.map((set, i) => {
                  const aVal = set.a === "" ? null : Number(set.a);
                  const bVal = set.b === "" ? null : Number(set.b);
                  const aLeads = aVal !== null && bVal !== null && aVal > bVal;
                  const bLeads = aVal !== null && bVal !== null && bVal > aVal;
                  return (
                    <div key={i} className="rounded-xl border border-muted bg-muted/30 overflow-hidden">
                      {/* Quick scores row */}
                      <div className="flex items-center gap-1 flex-wrap px-3 pt-2 pb-1">
                        <Badge variant="outline" className="text-[11px] font-medium mr-1">Set {i + 1}</Badge>
                        {QUICK_RESULTS.map((r) => {
                          const activeA = aVal === r.a && bVal === r.b;
                          const activeB = aVal === r.b && bVal === r.a;
                          return (
                            <button
                              key={`${r.a}-${r.b}`}
                              type="button"
                              onClick={() => setSets((prev) => prev.map((s, idx) => idx === i ? { a: String(r.a), b: String(r.b) } : s))}
                              className={cn(
                                "h-6 px-1.5 rounded-md text-[11px] font-medium border transition-colors",
                                activeA ? "bg-blue-500 text-white border-blue-500"
                                  : activeB ? "bg-orange-500 text-white border-orange-500"
                                  : "bg-background hover:bg-muted border-border text-foreground"
                              )}
                              data-testid={`button-quick-${i}-${r.a}-${r.b}`}
                            >
                              {r.a}-{r.b}
                            </button>
                          );
                        })}
                        <button
                          type="button"
                          onClick={() => setSets((prev) => prev.map((s, idx) => idx === i ? { a: s.b, b: s.a } : s))}
                          className="h-6 px-1.5 ml-auto rounded-md text-[11px] border border-border bg-background hover:bg-muted text-muted-foreground flex items-center gap-0.5"
                        >
                          Inv.
                        </button>
                      </div>
                      {/* Score selectors */}
                      <div className="grid grid-cols-[1fr_16px_1fr_28px] gap-1 px-3 pb-2">
                        <div className="space-y-1">
                          <div className="flex items-center gap-1">
                            <span className="w-1.5 h-1.5 rounded-full shrink-0 bg-blue-500" />
                            <span className="text-[10px] font-medium text-blue-700 dark:text-blue-400">Pareja 1</span>
                          </div>
                          <Select value={set.a} onValueChange={(v) => setSets((prev) => prev.map((s, idx) => idx === i ? { ...s, a: v } : s))}>
                            <SelectTrigger className={cn("h-10 text-xl font-bold text-center px-1 justify-center", aLeads && "border-2 border-blue-400/50 text-blue-700 dark:text-blue-400", bLeads && "text-muted-foreground")} data-testid={`select-set${i + 1}-a`}>
                              <SelectValue placeholder="—" />
                            </SelectTrigger>
                            <SelectContent>
                              {SCORE_OPTIONS.map((s) => <SelectItem key={s} value={String(s)}>{s}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex items-end justify-center pb-3">
                          <span className="text-lg font-bold text-muted-foreground">-</span>
                        </div>
                        <div className="space-y-1">
                          <div className="flex items-center gap-1">
                            <span className="w-1.5 h-1.5 rounded-full shrink-0 bg-orange-500" />
                            <span className="text-[10px] font-medium text-orange-700 dark:text-orange-400">Pareja 2</span>
                          </div>
                          <Select value={set.b} onValueChange={(v) => setSets((prev) => prev.map((s, idx) => idx === i ? { ...s, b: v } : s))}>
                            <SelectTrigger className={cn("h-10 text-xl font-bold text-center px-1 justify-center", bLeads && "border-2 border-orange-400/50 text-orange-700 dark:text-orange-400", aLeads && "text-muted-foreground")} data-testid={`select-set${i + 1}-b`}>
                              <SelectValue placeholder="—" />
                            </SelectTrigger>
                            <SelectContent>
                              {SCORE_OPTIONS.map((s) => <SelectItem key={s} value={String(s)}>{s}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex items-end justify-center pb-1">
                          {sets.length > 1 ? (
                            <button type="button" onClick={() => setSets((prev) => prev.filter((_, idx) => idx !== i))} className="w-6 h-6 flex items-center justify-center rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors">
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          ) : <div className="w-6" />}
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Add set */}
                {sets.length < 3 && (
                  <button type="button" onClick={() => setSets((prev) => [...prev, { a: "", b: "" }])} className="w-full h-8 rounded-lg border border-dashed border-border text-xs text-muted-foreground hover:bg-muted hover:text-foreground transition-colors flex items-center justify-center gap-1">
                    <Plus className="h-3 w-3" /> Añadir set
                  </button>
                )}

                {/* Live result preview */}
                {winnerLabel && (
                  <div className={cn("rounded-lg px-3 py-2 text-center text-sm font-semibold", aWins > bWins ? "bg-blue-500/10 text-blue-700 dark:text-blue-400" : bWins > aWins ? "bg-orange-500/10 text-orange-700 dark:text-orange-400" : "bg-muted text-muted-foreground")}>
                    {winnerLabel}
                  </div>
                )}
              </div>
            );
          })()}
          <DialogFooter className="shrink-0 pt-2">
            <Button variant="outline" onClick={() => setResultMatch(null)}>Cancelar</Button>
            <Button onClick={submitResult} disabled={resultMutation.isPending} data-testid="button-save-result">
              {resultMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete tournament AlertDialog */}
      <AlertDialog open={showDelete} onOpenChange={setShowDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar torneo?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminarán el torneo, todas sus parejas y todos sus partidos. Esta acción no se puede deshacer.
              {tournament && pairs.length > 0 && " Los resultados que ya afectaron el ranking de ELO no se revertirán."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteTournamentMutation.mutate()}
              data-testid="button-confirm-delete-tournament"
            >
              {deleteTournamentMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function TournamentPodiumCard({
  knockoutMatches,
  pairById,
}: {
  knockoutMatches: TMatch[];
  pairById: Map<string, Pair>;
}) {
  if (!knockoutMatches.length) return null;
  const maxRound = Math.max(...knockoutMatches.map((m) => m.round));
  // The final is always matchNumber 1 in the last round; matchNumber 2 is
  // the optional 3rd-place match.
  const finalMatch = knockoutMatches.find(
    (m) => m.round === maxRound && m.matchNumber === 1 && m.status === "completed",
  );
  if (!finalMatch || !finalMatch.winnerId) return null;

  const championId = finalMatch.winnerId;
  const finalistId = championId === finalMatch.pairAId ? finalMatch.pairBId : finalMatch.pairAId;
  const champion = championId ? pairById.get(championId) : null;
  const finalist = finalistId ? pairById.get(finalistId) : null;

  const thirdPlaceMatch = knockoutMatches.find(
    (m) => m.round === maxRound && m.matchNumber === 2 && m.status === "completed",
  );
  const thirdId = thirdPlaceMatch?.winnerId ?? null;
  const third = thirdId ? pairById.get(thirdId) : null;

  return (
    <div
      className="rounded-xl border border-amber-300/60 bg-gradient-to-br from-amber-50 to-yellow-50/60 dark:from-amber-950/30 dark:to-yellow-950/20 dark:border-amber-700/40 p-5"
      data-testid="podium-card"
    >
      <div className="flex items-center gap-2 mb-4 justify-center">
        <Trophy className="h-5 w-5 text-amber-500" />
        <span className="font-bold text-base text-amber-700 dark:text-amber-400">Podio Final</span>
      </div>

      <div className="flex items-end justify-center gap-6">
        {/* Finalist — 2nd place */}
        <div className="flex flex-col items-center gap-2">
          <div className="flex -space-x-2">
            {[finalist?.player1, finalist?.player2].filter(Boolean).map((pl, i) => (
              <Avatar key={i} className="h-9 w-9 border-2 border-background">
                <AvatarImage src={resolveAssetUrl(pl!.image ?? undefined)} />
                <AvatarFallback className="text-xs">{pl!.name?.charAt(0) ?? "?"}</AvatarFallback>
              </Avatar>
            ))}
          </div>
          <div className="flex flex-col items-center">
            <div className="flex items-center justify-center w-14 h-10 rounded-t-md bg-slate-200 dark:bg-slate-700">
              <Medal className="h-4 w-4 text-slate-500 dark:text-slate-400" />
            </div>
            <div className="bg-slate-200 dark:bg-slate-700 w-14 h-1 rounded-b-sm" />
          </div>
          <span className="text-xs font-medium text-muted-foreground text-center max-w-[90px] truncate">
            {finalist?.name ?? "—"}
          </span>
          <span className="text-[11px] text-muted-foreground">2.° lugar</span>
        </div>

        {/* Champion — 1st place */}
        <div className="flex flex-col items-center gap-2">
          <span className="text-2xl">👑</span>
          <div className="flex -space-x-2">
            {[champion?.player1, champion?.player2].filter(Boolean).map((pl, i) => (
              <Avatar key={i} className="h-12 w-12 border-2 border-amber-400">
                <AvatarImage src={resolveAssetUrl(pl!.image ?? undefined)} />
                <AvatarFallback>{pl!.name?.charAt(0) ?? "?"}</AvatarFallback>
              </Avatar>
            ))}
          </div>
          <div className="flex flex-col items-center">
            <div className="flex items-center justify-center w-16 h-14 rounded-t-md bg-amber-400 dark:bg-amber-500">
              <Trophy className="h-5 w-5 text-amber-950" />
            </div>
            <div className="bg-amber-400 dark:bg-amber-500 w-16 h-1 rounded-b-sm" />
          </div>
          <span className="text-sm font-semibold text-center max-w-[110px] truncate">
            {champion?.name ?? "—"}
          </span>
          <span className="text-[11px] text-amber-600 dark:text-amber-400 font-medium">Campeón</span>
        </div>

        {/* 3rd place (optional consolation final) */}
        {third && (
          <div className="flex flex-col items-center gap-2" data-testid="podium-third-place">
            <div className="flex -space-x-2">
              {[third.player1, third.player2].filter(Boolean).map((pl, i) => (
                <Avatar key={i} className="h-8 w-8 border-2 border-background">
                  <AvatarImage src={resolveAssetUrl(pl!.image ?? undefined)} />
                  <AvatarFallback className="text-xs">{pl!.name?.charAt(0) ?? "?"}</AvatarFallback>
                </Avatar>
              ))}
            </div>
            <div className="flex flex-col items-center">
              <div className="flex items-center justify-center w-14 h-7 rounded-t-md bg-orange-200 dark:bg-orange-900/60">
                <Medal className="h-4 w-4 text-orange-600 dark:text-orange-400" />
              </div>
              <div className="bg-orange-200 dark:bg-orange-900/60 w-14 h-1 rounded-b-sm" />
            </div>
            <span className="text-xs font-medium text-muted-foreground text-center max-w-[90px] truncate">
              {third.name}
            </span>
            <span className="text-[11px] text-muted-foreground">3.er lugar</span>
          </div>
        )}
      </div>
    </div>
  );
}

function scoreText(match: TMatch) {
  if (!match.score?.sets?.length) return null;
  return match.score.sets.map((s) => `${s.pairA}-${s.pairB}`).join(" ");
}

function auditHint(match: TMatch, memberName: (id: string | null) => string) {
  if (match.editedById) return `Editado por ${memberName(match.editedById)}`;
  if (match.recordedById) return `Registrado por ${memberName(match.recordedById)}`;
  return null;
}

interface MatchRowProps {
  match: TMatch;
  pairName: (id: string | null) => string;
  editable: boolean;
  onResult: (m: TMatch, mode: "record" | "edit") => void;
  memberName: (id: string | null) => string;
}

function MatchRow({ match, pairName, editable, onResult, memberName }: MatchRowProps) {
  const completed = match.status === "completed";
  const score = scoreText(match);
  const hint = auditHint(match, memberName);
  const canRecord = editable && !!match.pairAId && !!match.pairBId;
  return (
    <div className="flex items-center justify-between gap-2 rounded-md bg-muted/40 p-2 text-sm" data-testid={`match-row-${match.id}`}>
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={cn(match.winnerId === match.pairAId && "font-bold text-green-600")}>{pairName(match.pairAId)}</span>
          <span className="text-muted-foreground text-xs">vs</span>
          <span className={cn(match.winnerId === match.pairBId && "font-bold text-green-600")}>{pairName(match.pairBId)}</span>
          {score && <span className="text-muted-foreground">· {score}</span>}
        </div>
        {hint && <p className="text-[11px] text-muted-foreground mt-0.5">{hint}</p>}
      </div>
      {canRecord && (
        <Button
          size="sm"
          variant={completed ? "ghost" : "outline"}
          className="shrink-0 gap-1"
          onClick={() => onResult(match, completed ? "edit" : "record")}
          data-testid={`button-result-${match.id}`}
        >
          {completed ? <Pencil className="h-3.5 w-3.5" /> : null}
          {completed ? "Editar" : "Registrar"}
        </Button>
      )}
    </div>
  );
}

function BracketMatch({ match, pairName, editable, onResult, memberName }: MatchRowProps) {
  const completed = match.status === "completed";
  const score = scoreText(match);
  const hint = auditHint(match, memberName);
  const isBye = completed && !match.score && (!match.pairAId || !match.pairBId);
  const canRecord = editable && !!match.pairAId && !!match.pairBId && !isBye;
  return (
    <div className="rounded-md border p-2 text-sm space-y-1" data-testid={`bracket-match-${match.id}`}>
      <div className="flex items-center justify-between gap-1">
        <span className={cn("truncate", match.winnerId === match.pairAId && "font-bold text-green-600")}>
          {pairName(match.pairAId)}
        </span>
        {score && <span className="text-xs text-muted-foreground">{match.score?.sets.map((s) => s.pairA).join("/")}</span>}
      </div>
      <div className="flex items-center justify-between gap-1">
        <span className={cn("truncate", match.winnerId === match.pairBId && "font-bold text-green-600")}>
          {pairName(match.pairBId)}
        </span>
        {score && <span className="text-xs text-muted-foreground">{match.score?.sets.map((s) => s.pairB).join("/")}</span>}
      </div>
      {isBye && <p className="text-[11px] text-muted-foreground">BYE</p>}
      {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
      {canRecord && (
        <Button
          size="sm"
          variant={completed ? "ghost" : "outline"}
          className="w-full h-7 gap-1"
          onClick={() => onResult(match, completed ? "edit" : "record")}
          data-testid={`button-bracket-result-${match.id}`}
        >
          {completed ? <><Pencil className="h-3 w-3" /> Editar</> : "Registrar"}
        </Button>
      )}
    </div>
  );
}
